import { exhaustive } from "exhaustive"
import { FC, useEffect, useState } from "react"
import { useClient } from "../../Client/ClientAndUserProvider"
import { PenIcon } from "../../Icons/Icon"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import {
	AddEditOrderEmailReceiverModal,
	OrderReceiverEmailModalMode,
} from "./AddEditOrderEmailReceiverModal/AddEditOrderEmailReceiverModal"
import style from "./CustomerPortalClientOrderEmailReceivers.module.css"
import { GetOrderEmailReceiver, GetOrderEmailReceivers } from "./types"

type CustomerPortalClientOrderReceiverEmailsProps = {}

export const CustomerPortalClientOrderEmailReceivers: FC<CustomerPortalClientOrderReceiverEmailsProps> = (
	props: CustomerPortalClientOrderReceiverEmailsProps,
) => {
	const client = useClient()
	const permissions = usePermissions()

	const [receivers, setReceivers] = useState<GetOrderEmailReceiver[]>([])
	const [editReceiver, setEditReceiver] = useState<"new" | GetOrderEmailReceiver | null>(null)

	const [dataLoading, setDataLoading] = useState(true)

	useEffect(() => {
		fetchReceivers()
	}, [])

	function fetchReceivers() {
		setDataLoading(true)
		setReceivers([])

		if (!permissions.isAllowed(PermissionAreaLocation.Client_Information_View)) {
			setDataLoading(false)
			return
		}

		API.getWithRetries<GetOrderEmailReceivers>(`/customer-portal/order-email-receivers-v1/${client.identifier}`, true)
			.then((res) => {
				setReceivers(res.receivers)
				setDataLoading(false)
			})
			.catch(() => {
				setReceivers([])
				setDataLoading(false)
			})
	}

	function addEditOrderEmailReceiverModal() {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Information_Update) || !editReceiver) {
			return null
		}

		let mode: OrderReceiverEmailModalMode

		if (editReceiver === "new") {
			mode = {
				type: "Add",
			}
		} else {
			mode = {
				type: "Edit",
				receiver: editReceiver,
			}
		}

		return (
			<AddEditOrderEmailReceiverModal
				mode={mode}
				onClose={() => {
					setEditReceiver(null)
				}}
				onDone={() => {
					fetchReceivers()
					setEditReceiver(null)
				}}
			/>
		)
	}

	function receiversContent() {
		const allowedToEdit = permissions.isAllowed(PermissionAreaLocation.Client_Information_Update)

		return (
			<div className={style.contentWrapper}>
				<div className={style.header}>E-poster för ordermottagning</div>
				<div className={style.underHeader}>
					Här kan du sätta flera olika e-poster och styra vart e-posten skickas, baserat på kategori. Om det inte
					finns någon mottagare satt här så används det gamla värdet från "E-post för ordermottagning" under
					kontaktdetaljer.
					<br />
					<br />
					<strong>
						Viktigt: Om det finns minst en mottagare satt, men inte en mottagare för "Alla kategorier" och en
						beställning med en kategori som saknar mottagare läggs så kommer inget e-postmeddelande att skickas
						för den beställningen.
					</strong>
				</div>
				<div className={style.receiversWrapper}>
					{receivers.map((receiver) => {
						const categoryNames = exhaustive(receiver.categoriesToReceiveMailsFor, "type", {
							AllCategories: () => {
								return "Alla kategorier"
							},
							SpecificCategories: (it) => {
								return it.categories.map((x) => x.categoryName).join(", ")
							},
							_: () => "",
						})

						return (
							<div
								key={receiver.id}
								className={style.orderEmailReceiver}
								onClick={() => {
									setEditReceiver(receiver)
								}}>
								<span>
									{receiver.email} - <strong>{categoryNames}</strong>
								</span>

								{allowedToEdit ? <PenIcon size={16} iconClassName={style.editIcon} /> : null}
							</div>
						)
					})}
				</div>
				<div
					className={style.addReceiverButton}
					onClick={() => {
						setEditReceiver("new")
					}}>
					Lägg till
					<span style={{ fontSize: "24px" }}>+</span>
				</div>
			</div>
		)
	}

	if (!permissions.isAllowed(PermissionAreaLocation.Client_Information_View)) {
		return null
	}

	return (
		<div className={style.wrapper}>
			{dataLoading ? (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Loader />
				</div>
			) : (
				<>
					{addEditOrderEmailReceiverModal()}
					{receiversContent()}
				</>
			)}
		</div>
	)
}
